import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';
import { notifyAndTrackError } from '../../../../common/error';
import { ProcessingComponent } from '../../../../components';
import { LockInitialWidthDirective } from '../../../../directives/lock-initial-width.directive';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { IPocCatalogAddInsect } from '../../../../services/insecta/poc/poc.models';
import { PocService } from '../../../../services/insecta/poc/poc.service';
import { PocContainerInfestationRateComponent } from '../poc-container-infestation-rate/poc-container-infestation-rate.component';
import { PocInsectItemComponent } from '../poc-insect-item/poc-insect-item.component';
import { isEmpty } from '../../../../common/functions';

@Component({
  selector: 'app-poc-order-insect',
  standalone: true,
  imports: [
    PocInsectItemComponent,
    PocContainerInfestationRateComponent,
    FloatLabelModule,
    DatePickerModule,
    CommonModule,
    FormsModule,
    InputTextModule,
    ToggleSwitchModule,
    TextareaModule,
    SelectModule,
    ButtonModule,
    ProcessingComponent,
    DialogModule,
    TooltipModule,
    LockInitialWidthDirective
  ],
  templateUrl: './poc-order-insect.component.html'
})
export class PocOrderInsectComponent {
  private readonly msgService = inject(MessageService);
  private readonly appInsights = inject(AppInsightsService);
  private readonly pocService = inject(PocService);

  @Input() editingInstance: boolean = false;
  @Input() editingMode!: string;
  @Input() modalTitle: string = '';
  @Input() projects!: any[];
  @Input() projectTooltipText: string = '';
  @Input() selectedSite: any = null;
  @Output() public dialogCloseEvent = new EventEmitter();
  @Output() public dialogCancelEvent = new EventEmitter();

  isProcessing = false;
  orderInsect: {
    deliveryDate: Date | null;
    note: any;
    projectId: any;
    insectItem: any;
    containerItem: any;
    recurring: boolean;
  } = {
    deliveryDate: null,
    note: null,
    projectId: null,
    insectItem: {},
    containerItem: {},
    recurring: false
  };

  catalogAddInsect: IPocCatalogAddInsect | null = null;
  distinctContainers: any[] = [];
  distinctInsects: any[] = [];
  distinctInsectVariants: any[] = [];
  distinctGrowthStages: any[] = [];
  idMaps: any[] = [];

  insectInfestationReset: boolean = false;
  insectGrowthStageSelected: boolean = false;
  containerDisabled: boolean = true;

  ngOnInit(): void {
    this.isProcessing = true;
    firstValueFrom(this.pocService.pocGetCatalogAddInsect(this.selectedSite))
      .then((response: any) => {
        this.catalogAddInsect = response;
        console.log('Catalog Add Insect:', this.catalogAddInsect);

        // Populate distinctInsects
        this.distinctInsects = (response.catalogInsects || []).map((ci: any) => ({
          catalogInsectId: ci.id,
          insectId: ci.insectId,
          scientificName: ci.insect?.scientificName || ''
        }));
        console.log('Distinct insects:', this.distinctInsects);

        // Populate distinctInsectVariants
        this.distinctInsectVariants = (response.catalogInsects || [])
          .flatMap((ci: any) =>
            (ci.catalogInsectVariants || []).map((civ: any) => ({
              variantId: civ.id,
              catalogInsectId: civ.catalogInsectId,
              insectVariantId: civ.insectVariantId,
              description: civ.insectVariant?.description || '',
              variantTypeId: civ.insectVariant?.variantTypeId,
              variantTypeName: civ.insectVariant?.variantType?.name || '',
              insectId: ci.insectId,
              scientificName: ci.insect?.scientificName || '',
              displayName: `${ci.insect?.scientificName || ''} (${civ.insectVariant?.variantType?.name || ''})`
            }))
          )
          .filter(
            (civ: any, idx: number, arr: any[]) => civ.insectVariantId && arr.findIndex((vv) => vv.insectVariantId === civ.insectVariantId) === idx
          );
        console.log('Distinct insect variants:', this.distinctInsectVariants);

        // Populate distinctGrowthStages
        this.distinctGrowthStages = (response.catalogInsects || [])
          .flatMap((ci: any) => ci.catalogInsectGrowthStages || [])
          .map((gs: any) => ({
            insectGrowthStageId: gs.insectGrowthStage?.id,
            growthStageId: gs.insectGrowthStage?.growthStageId,
            name: gs.insectGrowthStage?.growthStage?.name || ''
          }))
          .filter(
            (gs: any, idx: number, arr: any[]) =>
              gs.insectGrowthStageId && arr.findIndex((g) => g.insectGrowthStageId === gs.insectGrowthStageId) === idx
          );
        console.log('Distinct growth stages:', this.distinctGrowthStages);

        // Populate distinctContainers
        this.distinctContainers = (response.catalogInsects || [])
          .flatMap((ci: any) => ci.catalogInsectGrowthStages || [])
          .flatMap((gs: any) => gs.catalogContainerInsectGrowthStages || [])
          .map((c: any) => ({
            containerId: c.containerId,
            name: c.container?.name || ''
          }))
          .filter((c: any, idx: number, arr: any[]) => c.containerId && arr.findIndex((cc) => cc.containerId === c.containerId) === idx);
        console.log('Distinct containers:', this.distinctContainers);

        // Populate idMaps
        this.idMaps = (response.catalogInsects || [])
          .flatMap((ci: any) =>
            (ci.catalogInsectGrowthStages || []).flatMap((gs: any) =>
              (gs.catalogContainerInsectGrowthStages || []).map((c: any) => ({
                catalogInsectGrowthStageId: c.catalogInsectGrowthStageId,
                catalogInsectId: gs.catalogInsectId,
                insectGrowthStageId: gs.insectGrowthStageId
              }))
            )
          )
          .filter(
            (m: any, idx: number, arr: any[]) =>
              m.catalogInsectGrowthStageId &&
              arr.findIndex(
                (mm) =>
                  mm.catalogInsectGrowthStageId === m.catalogInsectGrowthStageId &&
                  mm.catalogInsectId === m.catalogInsectId &&
                  mm.insectGrowthStageId === m.insectGrowthStageId
              ) === idx
          );
        console.log('Unique ID mappings:', this.idMaps);

        this.isProcessing = false;
      })
      .catch((err) => {
        this.isProcessing = false;
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'PocOrderDetailsComponent', action: 'OpenInsectModal:Error' });
      });
  }

  onCancel() {
    this.dialogCancelEvent.emit(false);
  }

  onSaveOrderItem() {}

  onReceiveInsectData(event: any): void {
    if (isEmpty(event.insectGrowthStageId) || isEmpty(event.insectVariantId)) {
      this.insectInfestationReset = true;
      this.orderInsect.deliveryDate = null;
      this.orderInsect.insectItem = {};
      this.orderInsect.containerItem = {};
      this.orderInsect.recurring = false;
      this.insectGrowthStageSelected = false;
      this.containerDisabled = true;
      // this.removeRecurrences();
      // this.computeInsectValidation(false);
      return;
    }

    // insectGrowthStageId: "ca0359c4-d2a2-f011-9eb0-6045bd7aa4e2"
    // insectVariantId: "c80359c4-d2a2-f011-9eb0-6045bd7aa4e2"

    const idMap = this.idMaps.find((x) => x.insectGrowthStageId === event.insectGrowthStageId);
    console.log('ID Map found:', idMap);

    // const blah = this.catalogAddInsect?.catalogInsects.find((ci: any) => ci.id === idMap.catalogInsectId);
    // console.log('Catalog Insect found:', blah);

    const catalogInsect = this.catalogAddInsect?.catalogInsects.find((ci: any) => ci.id === idMap.catalogInsectId);
    console.log('Catalog Insect found:', catalogInsect);
    const containers: any[] = [];

    if (catalogInsect && Array.isArray(catalogInsect.catalogInsectGrowthStages)) {
      catalogInsect.catalogInsectGrowthStages.forEach((gs: any) => {
        if (Array.isArray(gs.catalogContainerInsectGrowthStages)) {
          gs.catalogContainerInsectGrowthStages.forEach((c: any) => {
            if (c.containerId) {
              containers.push({
                id: c.id,
                containerId: c.containerId,
                name: c.container?.name || '',
                infestationRate: c.infestationRate
              });
            }
          });
        }
      });
    }

    // Deduplicate by containerId
    const distinctContainers = containers.filter((c, idx, arr) => arr.findIndex((cc) => cc.containerId === c.containerId) === idx);
    console.log('Distinct containers:', distinctContainers);
    this.distinctContainers = distinctContainers;
    this.containerDisabled = false;

    // this.selectedCatalogInsectGrowthStageId = event.catalogInsectGrowthStageId;

    // const newGSId = event.insectGrowthStageId;
    // const newVarId = event.insectVariantId;
    // if (this.currentInsectGrowthStageId === newGSId && this.currentInsectVariantId === newVarId) {
    //   this.orderInsect.insectItem = cloneDeep(event);
    //   this.computeInsectValidation(false);
    // } else {
    //   this.isProcessing = true;
    //   this.insectGrowthStageSelected = false;
    //   this.removeRecurrences();
    //   this.orderInsect.insectItem = cloneDeep(event);
    //   this.currentInsectGrowthStageId = this.orderInsect.insectItem.insectGrowthStageId;
    //   this.currentInsectVariantId = this.orderInsect.insectItem.insectVariantId;
    //   if (this.editing) {
    //     this.editingOrderItemContainerInfestation = {
    //       catalogContainerInsectGrowthStageId: this.editingOrderItemDetails.catalogContainerInsectGrowthStageId,
    //       infestationRateQuantity: this.editingInstance
    //         ? this.selectedEditingDetailsOrderInstance.infestationRateQuantity
    //         : this.editingOrderItemDetails.infestationRateQuantity,
    //       standardInfestationRate: this.editingInstance
    //         ? this.selectedEditingDetailsOrderInstance.standardInfestationRate
    //         : this.editingOrderItemDetails.standardInfestationRate
    //     };
    //   }
    //   firstValueFrom(
    //     this.orderItemTypeService.getRecurringOrderItemTypesConstraints(
    //       this.catalogId,
    //       this.insectOrderItemType.OrderItemTypeId,
    //       this.orderInsect.insectItem.insectVariantId,
    //       this.orderInsect.insectItem.insectGrowthStageId,
    //       null
    //     )
    //   )
    //     .then((data) => {
    //       this.leadTime = data.maxLeadTime;
    //       const DEFAULT_MIN_DATE = parseLocalDate(new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
    //       const DEFAULT_MAX_DATE = new Date(2100, 11, 31);
    //       this.minDate = this.canApprove ? DEFAULT_MIN_DATE : data.minDate === null ? DEFAULT_MIN_DATE : parseLocalDate(data.minDate);
    //       this.maxDate = data.maxDate === null ? DEFAULT_MAX_DATE : parseLocalDate(data.maxDate);
    //       this.invalidDays = [0, 1, 2, 3, 4, 5, 6].filter((day) => !data.availableDays.includes(day));
    //       this.unavailableDates = data.unavailableDates.map(parseLocalDate);
    //       this.unavailableDatesIsoStrings = this.unavailableDates.map((date) => {
    //         const yyyy = date.getFullYear();
    //         const mm = String(date.getMonth() + 1).padStart(2, '0');
    //         const dd = String(date.getDate()).padStart(2, '0');
    //         return `${yyyy}-${mm}-${dd}`;
    //       });
    //     })
    //     .catch((err) => {
    //       notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderInsectComponent', action: 'LoadRecurringConstraints' });
    //     })
    //     .finally(() => {
    //       if (this.editing) {
    //         let firstDeliveryDate: Date | null = null;
    //         if (this.editingInstance) {
    //           if (this.selectedEditingDetailsOrderInstance && this.selectedEditingDetailsOrderInstance.deliveryDate) {
    //             firstDeliveryDate = parseLocalDate(this.selectedEditingDetailsOrderInstance.deliveryDate);
    //           }
    //         } else {
    //           if (this.editingOrderItemDetails && this.editingOrderItemDetails.firstDeliveryDate) {
    //             firstDeliveryDate = parseLocalDate(this.editingOrderItemDetails.firstDeliveryDate);
    //           }
    //         }
    //         if (firstDeliveryDate && firstDeliveryDate < this.minDate && !this.canApprove) {
    //           warningMsg(this.msgService, 'The first delivery date for this Order Item is no longer valid and must be reselected');
    //           this.orderInsect.deliveryDate = null;
    //           this.selectedDates = [];
    //           this.recurrenceSchedule = undefined;
    //         } else {
    //           this.orderInsect.deliveryDate = firstDeliveryDate;
    //           if (
    //             (this.editingOrderItemDetails && this.editingOrderItemDetails.orderItemInstances) ||
    //             (this.editingInstance && this.editingMode === 'future' && this.status === 'Updated')
    //           ) {
    //             this.subsetSelectedDates = this.getSubsetSelectedDates();
    //             this.selectedDates = this.editingInstance
    //               ? this.subsetSelectedDates
    //               : this.editingOrderItemDetails.orderItemInstances.map((inst: { deliveryDate: any }) => inst.deliveryDate);
    //             this.recurrenceSchedule = this.editingOrderItemDetails.recurrenceSchedule;
    //             this.updateShowRecurringDatesTooltip();
    //           }
    //         }
    //         this.editing = false;
    //       } else {
    //         this.orderInsect.deliveryDate = null;
    //       }
    //       this.insectGrowthStageSelected = true;
    //       this.isProcessing = false;
    //       this.computeInsectValidation(false);
    //     });
    // }
  }
}
